import {
  type Control,
  type FieldPath,
  type FieldValues,
  useWatch,
  useFormState,
} from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { useEffect } from "react";
import { FormTooltip } from "@/components/ui/form-description";

interface EmailFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  description?: string;
  descriptionAsTooltip?: boolean;
  label: string;
  name: TName;
  placeholder?: string;
  required?: boolean;
  requiredLabel?: string;
  visuallyHiddenLabel?: boolean;
  disabled?: boolean;
  sharedMailboxWarning?: boolean;
  sharedMailboxWarningTitle?: string;
  sharedMailboxWarningDescription?: string;
  sharedMailboxKeywords?: string[];
  onSharedMailboxWarningChange?: (hasWarning: boolean) => void;
}

export function EmailField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  description,
  descriptionAsTooltip = false,
  label,
  name,
  placeholder,
  required,
  requiredLabel = "Required",
  visuallyHiddenLabel = false,
  disabled = false,
  sharedMailboxWarning = false,
  sharedMailboxWarningTitle = "Shared Mailbox Detected",
  sharedMailboxWarningDescription = "This email address appears to be for a shared mailbox rather than a personal one. We recommend using a personal email address. If you wish to proceed with this email anyway, click the button below.",
  sharedMailboxKeywords = [
    "info",
    "contact",
    "support",
    "admin",
    "sales",
    "help",
    "team",
    "office",
    "general",
    "noreply",
    "no-reply",
  ],
  onSharedMailboxWarningChange,
  className,
  ...htmlProps
}: Readonly<EmailFieldProps<TFieldValues, TName>>) {
  const emailValue = useWatch({ control, name });
  const { isSubmitted } = useFormState({ control });

  const checkForSharedMailbox = (email: string): boolean => {
    if (!email || !sharedMailboxWarning) return false;

    const localPart = email.split("@")[0]?.toLowerCase();
    if (!localPart) return false;

    return sharedMailboxKeywords.some((keyword) =>
      localPart.includes(keyword.toLowerCase()),
    );
  };

  const hasSharedMailbox = checkForSharedMailbox(emailValue as string);

  // Only show warning after form has been submitted at least once
  const shouldShowWarning = hasSharedMailbox && isSubmitted;

  useEffect(() => {
    onSharedMailboxWarningChange?.(shouldShowWarning);
  }, [shouldShowWarning, onSharedMailboxWarningChange]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className} {...htmlProps}>
          <div className={cn(visuallyHiddenLabel ? "sr-only" : "")}>
            <FormLabel className="inline-block leading-snug">
              {label}
              {required ? (
                <>
                  <span className="ms-1 text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">({requiredLabel})</span>
                </>
              ) : null}
            </FormLabel>
            {description && !descriptionAsTooltip ? (
              <FormDescription>{description}</FormDescription>
            ) : null}
            {description && descriptionAsTooltip && (
              <FormTooltip description={description} />
            )}
          </div>
          <FormControl>
            <Input
              type="text"
              aria-required={required ? "true" : "false"}
              placeholder={placeholder}
              inputMode="email"
              disabled={disabled}
              {...field}
            />
          </FormControl>
          <FormMessage />
          {shouldShowWarning && (
            <Alert variant="warning" className="mt-2">
              <AlertTriangle />
              <AlertTitle>{sharedMailboxWarningTitle}</AlertTitle>
              <AlertDescription>
                {sharedMailboxWarningDescription}
              </AlertDescription>
            </Alert>
          )}
        </FormItem>
      )}
    />
  );
}
